import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, CircularProgress, Table,
  TableHead, TableBody, TableRow, TableCell, Divider, Stack,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@apollo/client';
import { CONTRACT_SERVICE_ITEMS_QUERY } from '../graphql/queries';
import { fmtDate } from '../utils/dateUtils';
import { useCompanyDetails } from '../utils/companyDetails';

function fmtINR(n) {
  // Use actual INR symbol instead of unicode escape
  return '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

const STATUS_LABELS = {
  draft:                    'DRAFT',
  sent_for_review:          'SENT FOR REVIEW',
  user_requested_changes:   'CHANGES REQUESTED',
  user_agreed:              'USER AGREED',
  signed:                   'FULLY SIGNED',
  active:                   'ACTIVE',
  terminated:               'TERMINATED',
};

export default function ContractPrintView({ open, onClose, contract }) {
  const COMPANY = useCompanyDetails();
  const { data, loading } = useQuery(CONTRACT_SERVICE_ITEMS_QUERY, {
    variables: { contractId: contract?.id },
    skip: !open || !contract?.id,
    fetchPolicy: 'network-only',
  });

  if (!contract) return null;

  const items      = data?.contractServiceItems || [];
  const milestones = contract.milestones || [];

  const gross = items.reduce((s, i) => s + Number(i.subtotal      ?? 0), 0);
  const disc  = items.reduce((s, i) => s + Number(i.discountAmount ?? 0), 0);
  const tax   = items.reduce((s, i) => s + Number(i.taxAmount      ?? 0), 0);
  const net   = items.reduce((s, i) => s + Number(i.lineTotal      ?? 0), 0);

  const statusLabel = STATUS_LABELS[contract.status] || (contract.status || '').toUpperCase();
  const clientFirstName = contract.assignedUser?.profile?.firstName || '';
  const clientLastName  = contract.assignedUser?.profile?.lastName  || '';
  const clientName = contract.parties?.name || (clientFirstName + ' ' + clientLastName).trim() || contract.assignedUser?.email || 'Client';
  // The named parties: the vendor's business, or the bride and groom
  const pty = contract.parties;
  const partyRows = !pty ? [['Assigned To', clientName || '—']]
    : pty.type === 'vendor' ? [['Vendor', pty.vendorName || clientName]]
      : (pty.type === 'bride' || pty.type === 'groom') ? [['Bride', pty.brideName || '—'], ['Groom', pty.groomName || '—']]
        : [['Assigned To', clientName || '—']];
  const escHtml = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
  const refDisplay = contract.refNumber || ('#' + (contract.id || '').slice(-8).toUpperCase());

  const handlePrint = () => {
    const now  = new Date();
    const dateStr = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

    const milestoneRows = milestones.map((m, idx) => `
      <tr>
        <td>${idx + 1}</td>
        <td>${m.stageName}</td>
        <td style="text-align:right"><strong>${fmtINR(m.amount)}</strong></td>
        <td>${m.dueDate ? fmtDate(m.dueDate) : '—'}</td>
        <td>${m.paidAt  ? fmtDate(m.paidAt)  : '—'}</td>
        <td style="font-size:11px">${m.notes || '—'}</td>
        <td>${m.paidAt
          ? '<span style="color:green;font-weight:700">Paid</span>'
          : '<span style="color:#888">Pending</span>'}</td>
      </tr>`).join('');

    const itemRows = items.map(item => `
      <tr>
        <td>
          <strong>${item.serviceName}</strong><br/>
          <span style="font-size:11px;color:#666">${item.serviceCategory || ''} &bull; ${item.unit}</span>
        </td>
        <td style="text-align:right">${item.quantity}</td>
        <td style="text-align:right">${fmtINR(item.unitPrice)}</td>
        <td style="text-align:right;color:green">${Number(item.discountAmount) > 0 ? '-' + fmtINR(item.discountAmount) : '—'}</td>
        <td style="text-align:right">${fmtINR(item.taxAmount)}</td>
        <td style="text-align:right"><strong>${fmtINR(item.lineTotal)}</strong></td>
      </tr>`).join('');

    const adminSigBlock = contract.signatureData?.startsWith('data:')
      ? `<img src="${contract.signatureData}" alt="Admin signature" style="max-height:80px;display:block;margin-bottom:8px"/>`
      : `<div style="height:80px;border:1px dashed #ccc;border-radius:4px;display:flex;align-items:center;justify-content:center;margin-bottom:8px;color:#aaa;font-style:italic">[ Pending Signature ]</div>`;

    const clientSigBlock = contract.userSignatureData?.startsWith('data:')
      ? `<img src="${contract.userSignatureData}" alt="Client signature" style="max-height:80px;display:block;margin-bottom:8px"/>`
      : `<div style="height:80px;border:1px dashed #ccc;border-radius:4px;display:flex;align-items:center;justify-content:center;margin-bottom:8px;color:#aaa;font-style:italic">[ Pending Signature ]</div>`;

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Contract — ${contract.title}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family: Georgia, serif; font-size: 13px; color: #111; padding: 40px; background: #fff; }
    h1 { font-size: 22px; font-weight: 800; letter-spacing: 2px; }
    h2 { font-size: 14px; font-weight: 700; letter-spacing: 1px; border-bottom: 1px solid #333; padding-bottom: 4px; margin: 20px 0 10px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
    th, td { border: 1px solid #ddd; padding: 6px 8px; }
    th { background: #f0f0f0; font-weight: 700; }
    .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 12px; margin-bottom: 16px; }
    .subheader { text-align: center; margin-bottom: 20px; }
    .details-grid { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #333; border-radius: 4px; overflow: hidden; margin-bottom: 20px; }
    .detail-cell { padding: 8px 12px; border-bottom: 1px solid #e0e0e0; border-right: 1px solid #e0e0e0; }
    .detail-cell label { display: block; font-size: 11px; color: #666; margin-bottom: 2px; }
    .detail-cell .val { font-weight: 700; }
    .details-header { background: #222; color: white; padding: 8px 12px; grid-column: span 2; font-size: 12px; font-weight: 700; letter-spacing: 1px; }
    .body-box { white-space: pre-wrap; line-height: 1.9; margin-bottom: 20px; }
    .penalty { border: 1px solid #888; border-radius:4px; padding: 12px; background: #fff8f0; margin-bottom: 20px; }
    .cost-summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; background: #f5f5f5; border: 1px solid #ddd; border-radius: 4px; padding: 12px; margin-bottom: 8px; }
    .cost-summary .label { font-size: 11px; color: #666; }
    .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-top: 16px; }
    .sig-box { }
    .sig-line { border-top: 1px solid #333; padding-top: 6px; margin-top: 8px; font-size: 11px; }
    .footer { border-top: 1px solid #ccc; padding-top: 12px; text-align: center; font-size: 11px; color: #666; margin-top: 24px; }
    @page { margin: 20mm; }
    @media screen { body { max-width: 800px; margin: 0 auto; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>${COMPANY.name}</h1>
    <table style="width:auto;margin:8px auto 0;border-collapse:collapse;font-size:11px">
      <thead>
        <tr>
          <th style="padding:4px 10px;background:#f0f0f0;border:1px solid #ccc">Address</th>
          <th style="padding:4px 10px;background:#f0f0f0;border:1px solid #ccc">Phone</th>
          <th style="padding:4px 10px;background:#f0f0f0;border:1px solid #ccc">Email</th>
          <th style="padding:4px 10px;background:#f0f0f0;border:1px solid #ccc">Website</th>
          <th style="padding:4px 10px;background:#f0f0f0;border:1px solid #ccc">GSTIN</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="padding:4px 10px;border:1px solid #ccc">${COMPANY.address}</td>
          <td style="padding:4px 10px;border:1px solid #ccc">${COMPANY.phone}</td>
          <td style="padding:4px 10px;border:1px solid #ccc">${COMPANY.email}</td>
          <td style="padding:4px 10px;border:1px solid #ccc">${COMPANY.website}</td>
          <td style="padding:4px 10px;border:1px solid #ccc">${COMPANY.gstin}</td>
        </tr>
      </tbody>
    </table>
  </div>
  <div class="subheader">
    <h1>CONTRACT AGREEMENT</h1>
    <p style="font-size:12px;color:#555;margin-top:4px">STRICTLY CONFIDENTIAL | LEGALLY BINDING | Ref: ${refDisplay}</p>
  </div>

  <div class="details-grid">
    <div class="details-header">CONTRACT DETAILS</div>
    <div class="detail-cell"><label>Contract Title</label><span class="val">${contract.title}</span></div>
    <div class="detail-cell"><label>Contract ID</label><span class="val">${refDisplay}</span></div>
    ${partyRows.map(([k, v]) => `<div class="detail-cell"><label>${k}</label><span class="val">${escHtml(v)}</span></div>`).join('')}
    <div class="detail-cell"><label>Email</label><span>${contract.assignedUser?.email || '—'}</span></div>
    <div class="detail-cell"><label>Total Cost</label><span class="val">${contract.cost ? fmtINR(contract.cost) + '/-' : '—'}</span></div>
    <div class="detail-cell"><label>Due Date</label><span>${fmtDate(contract.dueDate) || '—'}</span></div>
    <div class="detail-cell"><label>Status</label><span class="val">${statusLabel}</span></div>
    <div class="detail-cell"><label>Created On</label><span>${fmtDate(contract.createdAt) || '—'}</span></div>
  </div>

  ${contract.body ? `
  <h2>CONTRACT BODY</h2>
  <div class="body-box">${(contract.body || '').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div>
  ` : ''}

  ${contract.terms ? `
  <h2>TERMS &amp; CONDITIONS</h2>
  <div class="body-box">${(contract.terms || '').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div>
  ` : ''}

  ${contract.penaltyClause ? `
  <div class="penalty">
    <h2 style="border:none;margin:0 0 8px">&#9632; PENALTY CLAUSE</h2>
    <div class="body-box" style="margin:0">${(contract.penaltyClause || '').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div>
  </div>
  ` : ''}

  ${(items.length > 0 || contract.cost) ? `
  <h2>COST DETAILS</h2>
  ${contract.cost ? `<p style="margin-bottom:8px">Total Consideration: <strong>${fmtINR(contract.cost)}/-</strong>${contract.costDescription ? ' — ' + contract.costDescription : ''}</p>` : ''}
  ${items.length > 0 ? `
  <p style="font-weight:700;margin-bottom:6px">Breakdown:</p>
  <table>
    <thead><tr><th>Service</th><th style="text-align:right">Qty</th><th style="text-align:right">Unit Price</th><th style="text-align:right">Discount</th><th style="text-align:right">Tax</th><th style="text-align:right">Total</th></tr></thead>
    <tbody>${itemRows}</tbody>
  </table>
  <div class="cost-summary">
    <div><div class="label">Subtotal</div><div>${fmtINR(gross)}</div></div>
    <div><div class="label">Discount</div><div style="color:green">-${fmtINR(disc)}</div></div>
    <div><div class="label">GST / Tax</div><div>${fmtINR(tax)}</div></div>
    <div><div class="label" style="font-weight:700">TOTAL (incl. GST)</div><div style="font-size:16px;font-weight:800">${fmtINR(net)}</div></div>
  </div>
  ` : ''}
  ` : ''}

  ${milestones.length > 0 ? `
  <h2>SCHEDULE A — PAYMENT MILESTONES</h2>
  <table>
    <thead><tr><th>#</th><th>Stage Name</th><th style="text-align:right">Amount</th><th>Due Date</th><th>Paid At</th><th>Notes</th><th>Status</th></tr></thead>
    <tbody>${milestoneRows}</tbody>
  </table>
  ` : ''}

  <h2>E-SIGNATURE BLOCK</h2>
  <p style="font-size:12px;color:#555;margin-bottom:16px">By the signatures below, both parties confirm they have read, understood, and agreed to all terms and conditions of this contract.</p>
  <div class="sig-grid">
    <div class="sig-box">
      <p style="font-weight:700;margin-bottom:4px">Authorized Signatory (Admin)</p>
      <p style="font-size:11px;color:#666;margin-bottom:8px">Admin — ${COMPANY.name}</p>
      ${adminSigBlock}
      <div class="sig-line">${contract.signedAt ? 'Signed: ' + fmtDate(contract.signedAt, 'datetime') : 'Not yet signed'}</div>
    </div>
    <div class="sig-box">
      <p style="font-weight:700;margin-bottom:4px">Client Signatory</p>
      <p style="font-size:11px;color:#666;margin-bottom:8px">${clientName}</p>
      ${clientSigBlock}
      <div class="sig-line">${contract.userSignedAt ? 'Signed: ' + fmtDate(contract.userSignedAt, 'datetime') : 'Not yet signed'}</div>
    </div>
  </div>

  <div class="footer">
    <p>This document was generated electronically by ${COMPANY.brandName} on ${dateStr} at ${timeStr} IST.</p>
    <p>E-signatures are legally binding under the Information Technology Act, 2000 (India).</p>
    <p>Contract Reference: ${refDisplay} | This is a legally binding document — please retain for your records.</p>
  </div>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=900,height=800');
    if (win) {
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 600);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            Contract Preview — {contract.title}
            {contract.refNumber && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                [{contract.refNumber}]
              </Typography>
            )}
          </Typography>
        </Stack>
      </DialogTitle>

      {loading ? (
        <DialogContent sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></DialogContent>
      ) : (
        <DialogContent dividers sx={{ p: 0 }}>
          {/* ── Company header ── */}
          <Box sx={{ bgcolor: '#1a237e', color: 'white', p: 2, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={800} letterSpacing={1} mb={1}>{COMPANY.name}</Typography>
            <Table size="small" sx={{ width: 'auto', mx: 'auto', '& td': { color: 'rgba(255,255,255,0.9)', border: '1px solid rgba(255,255,255,0.2)', px: 2, py: 0.5, fontSize: 12 }, '& th': { color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.2)', px: 2, py: 0.5, fontSize: 11, fontWeight: 700, bgcolor: 'rgba(0,0,0,0.25)' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Address</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Website</TableCell>
                  <TableCell>GSTIN</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{COMPANY.address}</TableCell>
                  <TableCell>{COMPANY.phone}</TableCell>
                  <TableCell>{COMPANY.email}</TableCell>
                  <TableCell>{COMPANY.website}</TableCell>
                  <TableCell>{COMPANY.gstin}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ p: 3 }}>
            <Box textAlign="center" mb={2.5}>
              <Typography variant="h6" fontWeight={700} letterSpacing={2}>CONTRACT AGREEMENT</Typography>
              <Typography variant="caption" color="text.secondary">
                STRICTLY CONFIDENTIAL | LEGALLY BINDING{contract.refNumber ? ` | Ref: ${contract.refNumber}` : ''}
              </Typography>
            </Box>

            {/* ── Contract details block ── */}
            <Box border="1px solid #333" borderRadius={1} mb={2.5} overflow="hidden">
              <Box bgcolor="#333" px={2} py={0.8}>
                <Typography variant="caption" fontWeight={700} color="white" letterSpacing={1}>CONTRACT DETAILS</Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                {[
                  ['Contract Title',  contract.title],
                  ['Contract ID',     refDisplay],
                  ...partyRows,
                  ['Email',           contract.assignedUser?.email || '—'],
                  ['Total Cost',      contract.cost ? fmtINR(contract.cost) + '/-' : '—'],
                  ['Due Date',        fmtDate(contract.dueDate) || '—'],
                  ['Status',         statusLabel],
                  ['Created On',      fmtDate(contract.createdAt) || '\u2014'],
                ].map(([label, val]) => (
                  <Box key={label} sx={{ p: 1.2, borderBottom: '1px solid #e0e0e0', borderRight: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{label}</Typography>
                    <Typography variant="body2" fontWeight={600}>{val}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* ── Contract body ── */}
            {contract.body && (
              <Box mb={2.5}>
                <Typography variant="subtitle2" fontWeight={700} borderBottom="1px solid #ccc" pb={0.5} mb={1}>CONTRACT BODY</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.9 }}>{contract.body}</Typography>
              </Box>
            )}

            {contract.terms && (
              <Box mb={2.5}>
                <Typography variant="subtitle2" fontWeight={700} borderBottom="1px solid #ccc" pb={0.5} mb={1}>TERMS &amp; CONDITIONS</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.9 }}>{contract.terms}</Typography>
              </Box>
            )}

            {contract.penaltyClause && (
              <Box mb={2.5} border="1px solid #e65100" borderRadius={1} p={2} bgcolor="#fff8f0">
                <Typography variant="subtitle2" fontWeight={700} color="warning.dark" mb={0.8}>&#9632; PENALTY CLAUSE</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.9 }}>{contract.penaltyClause}</Typography>
              </Box>
            )}

            {/* ── Cost details ── */}
            {(items.length > 0 || contract.cost) && (
              <Box mb={2.5}>
                <Typography variant="subtitle2" fontWeight={700} borderBottom="1px solid #ccc" pb={0.5} mb={1}>COST DETAILS</Typography>
                {contract.cost && (
                  <Typography variant="body2" mb={1}>
                    Total Consideration: <strong>{fmtINR(contract.cost)}/-</strong>
                    {contract.costDescription ? ` \u2014 ${contract.costDescription}` : ''}
                  </Typography>
                )}
                {items.length > 0 && (
                  <>
                    <Typography variant="body2" fontWeight={600} mb={0.5}>Breakdown:</Typography>
                    <Table size="small" sx={{ mb: 1, border: '1px solid #e0e0e0' }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Qty</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Unit Price</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Discount</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Tax</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map(item => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>{item.serviceName}</Typography>
                              <Typography variant="caption" color="text.secondary">{item.serviceCategory} &bull; {item.unit}</Typography>
                            </TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">{fmtINR(item.unitPrice)}</TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>
                              {Number(item.discountAmount) > 0 ? '-' + fmtINR(item.discountAmount) : '—'}
                            </TableCell>
                            <TableCell align="right">{fmtINR(item.taxAmount)}</TableCell>
                            <TableCell align="right"><strong>{fmtINR(item.lineTotal)}</strong></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, p: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', borderRadius: 1 }}>
                      <Box><Typography variant="caption" color="text.secondary">Subtotal</Typography><Typography variant="body2">{fmtINR(gross)}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Discount</Typography><Typography variant="body2" color="success.main">-{fmtINR(disc)}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">GST / Tax</Typography><Typography variant="body2">{fmtINR(tax)}</Typography></Box>
                      <Box><Typography variant="caption" fontWeight={700}>TOTAL (incl. GST)</Typography><Typography variant="h6" fontWeight={800} color="primary.main">{fmtINR(net)}</Typography></Box>
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* ── Milestones ── */}
            {milestones.length > 0 && (
              <Box mb={2.5}>
                <Typography variant="subtitle2" fontWeight={700} borderBottom="1px solid #ccc" pb={0.5} mb={1}>SCHEDULE A \u2014 PAYMENT MILESTONES</Typography>
                <Table size="small" sx={{ border: '1px solid #e0e0e0' }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Stage Name</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Paid At</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {milestones.map((m, idx) => (
                      <TableRow key={m.id} sx={{ bgcolor: m.paidAt ? '#f1f8e9' : 'transparent' }}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{m.stageName}</TableCell>
                        <TableCell align="right"><strong>{fmtINR(m.amount)}</strong></TableCell>
                        <TableCell><Typography variant="caption">{fmtDate(m.dueDate) || '—'}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{m.paidAt ? fmtDate(m.paidAt) : '—'}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{m.notes || '—'}</Typography></TableCell>
                        <TableCell>
                          {m.paidAt
                            ? <Typography variant="caption" color="success.main" fontWeight={700}>Paid</Typography>
                            : <Typography variant="caption" color="text.secondary">Pending</Typography>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* ── E-Signature Block ── */}
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>E-SIGNATURE BLOCK</Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              By the signatures below, both parties confirm they have read, understood, and agreed to all terms and conditions of this contract.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
              {/* Admin */}
              <Box>
                <Typography variant="body2" fontWeight={700} mb={0.5}>Authorized Signatory (Admin)</Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>Admin \u2014 {COMPANY.name}</Typography>
                {contract.signatureData?.startsWith('data:')
                  ? <Box sx={{ border: '1px solid #ccc', p: 1, borderRadius: 1, mb: 1, bgcolor: '#fafafa', display: 'inline-block' }}>
                      <img src={contract.signatureData} alt="Admin signature" style={{ maxHeight: 80, display: 'block' }} />
                    </Box>
                  : <Box sx={{ height: 80, border: '1px dashed #ccc', borderRadius: 1, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" color="text.disabled" fontStyle="italic">[ Pending Signature ]</Typography>
                    </Box>}
                <Box borderTop="1px solid #555" pt={0.5}>
                  <Typography variant="caption">{contract.signedAt ? `Signed: ${fmtDate(contract.signedAt, 'datetime')}` : 'Not yet signed'}</Typography>
                </Box>
              </Box>
              {/* Client */}
              <Box>
                <Typography variant="body2" fontWeight={700} mb={0.5}>Client Signatory</Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>{clientName}</Typography>
                {contract.userSignatureData?.startsWith('data:')
                  ? <Box sx={{ border: '1px solid #ccc', p: 1, borderRadius: 1, mb: 1, bgcolor: '#fafafa', display: 'inline-block' }}>
                      <img src={contract.userSignatureData} alt="Client signature" style={{ maxHeight: 80, display: 'block' }} />
                    </Box>
                  : <Box sx={{ height: 80, border: '1px dashed #ccc', borderRadius: 1, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" color="text.disabled" fontStyle="italic">[ Pending Signature ]</Typography>
                    </Box>}
                <Box borderTop="1px solid #555" pt={0.5}>
                  <Typography variant="caption">{contract.userSignedAt ? `Signed: ${fmtDate(contract.userSignedAt, 'datetime')}` : 'Not yet signed'}</Typography>
                </Box>
              </Box>
            </Box>

            {/* ── Footer ── */}
            <Box borderTop="1px solid #ccc" pt={1.5} mt={3} textAlign="center">
              <Typography variant="caption" color="text.secondary" display="block">
                This document was generated electronically by {COMPANY.brandName}. E-signatures are legally binding under the Information Technology Act, 2000 (India).
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Contract Reference: {refDisplay} | This is a legally binding document \u2014 please retain for your records.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      )}

      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>Close</Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} disabled={loading}>
          Print / Save as PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}
